import { useState, type ReactNode } from "react";
import { Route, Switch, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PosAuthProvider, usePosAuth } from "@/hooks/pos/use-pos-auth";
import AdminHeader from "./admin-header";
import Sidebar from "./sidebar";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { WebSocketProvider } from "@/providers/WebSocketProvider";

// Admin pages - POS
import Dashboard from "@/pages/admin/pos/dashboard";
import OrdersPage from "@/pages/admin/pos/orders";
import LiveOrdersPage from "@/pages/admin/pos/live-orders";
import ProductsPage from "@/pages/admin/pos/products";
import MenuTypesPage from "@/pages/admin/pos/menu-types";
import SubcategoriesPage from "@/pages/admin/pos/subcategories";
import ModifierGroupsPage from "@/pages/admin/pos/modifier-groups";
import DiscountsPage from "@/pages/admin/pos/discounts";
import CustomDrinksPage from "@/pages/admin/pos/custom-drinks";
import LocationsPage from "@/pages/admin/pos/locations";
import PosUsersPage from "@/pages/admin/pos/pos-users";
import ReportsPage from "@/pages/admin/pos/reports";
import ActivityLogsPage from "@/pages/admin/pos/activity-logs";

// Admin pages - Platform (existing)
import TransactionsPage from "@/pages/admin/TransactionsPage";
import WaiversPage from "@/pages/admin/WaiversPage";
import UsersPage from "@/pages/admin/UsersPage";
import EntryPassTemplatesPage from "@/pages/admin/EntryPassTemplatesPage";
import MembershipTemplatesPage from "@/pages/admin/MembershipTemplatesPage";
import TaxSettingsPage from "@/pages/admin/TaxSettingsPage";

const posQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Inner layout that renders after auth check.
 * Must be inside PosAuthProvider.
 */
function AdminLayoutInner() {
  const { isAuthenticated, loading } = usePosAuth();
  const [, navigate] = useLocation();

  const [uiState, setUiState] = usePagePersistentState("ui", {
    isSidebarOpen: true,
    sidebarWidth: "w-[248px]",
  });

  const toggleSidebar = () => {
    setUiState((prev) => {
      const isCurrentlyOpen = prev.isSidebarOpen;
      if (isCurrentlyOpen) {
        return { ...prev, isSidebarOpen: false, sidebarWidth: "w-[54px]" };
      }
      return { ...prev, isSidebarOpen: true, sidebarWidth: "w-[248px]" };
    });
  };

  // Show loading spinner while checking stored session
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Redirect to admin login if not authenticated
  if (!isAuthenticated) {
    navigate("/admin/login", { replace: true });
    return null;
  }

  return (
    <div className="pos-admin platform-admin flex flex-col h-screen overflow-hidden">
      <AdminHeader onToggleSidebar={toggleSidebar} />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          width={uiState.sidebarWidth || "w-[248px]"}
          isOpen={uiState.isSidebarOpen !== false}
        />

        <main className="flex-1 overflow-y-auto bg-[var(--primary-background-color)]">
          <Switch>
            {/* Admin routes */}
            <Route path="/admin" component={Dashboard} />
            <Route path="/admin/orders" component={OrdersPage} />
            <Route path="/admin/live-orders" component={LiveOrdersPage} />
            <Route path="/admin/products" component={ProductsPage} />
            <Route path="/admin/products/*" component={ProductsPage} />
            <Route path="/admin/menu-types" component={MenuTypesPage} />
            <Route path="/admin/menu-types/*" component={MenuTypesPage} />
            <Route path="/admin/subcategories" component={SubcategoriesPage} />
            <Route path="/admin/modifier-groups" component={ModifierGroupsPage} />
            <Route path="/admin/modifier-groups/*" component={ModifierGroupsPage} />
            <Route path="/admin/discounts" component={DiscountsPage} />
            <Route path="/admin/discounts/*" component={DiscountsPage} />
            <Route path="/admin/custom-drinks" component={CustomDrinksPage} />
            <Route path="/admin/custom-drinks/*" component={CustomDrinksPage} />
            <Route path="/admin/locations" component={LocationsPage} />
            <Route path="/admin/locations/*" component={LocationsPage} />
            <Route path="/admin/pos-users" component={PosUsersPage} />
            <Route path="/admin/pos-users/*" component={PosUsersPage} />
            <Route path="/admin/reports" component={ReportsPage} />
            <Route path="/admin/activity-logs" component={ActivityLogsPage} />

            {/* Platform admin routes */}
            <Route path="/admin/transactions" component={TransactionsPage} />
            <Route path="/admin/waivers" component={WaiversPage} />
            <Route path="/admin/platform-users" component={UsersPage} />
            <Route path="/admin/entry-passes" component={EntryPassTemplatesPage} />
            <Route path="/admin/memberships" component={MembershipTemplatesPage} />
            <Route path="/admin/tax-settings" component={TaxSettingsPage} />
            <Route path="/admin/tax-settings/*" component={TaxSettingsPage} />

            {/* Fallback */}
            <Route>
              <div className="p-8 text-center text-[var(--secondary-text-color)]">
                Page not found
              </div>
            </Route>
          </Switch>
        </main>
      </div>
    </div>
  );
}

// Bridge: provides WebSocketProvider with POS admin user credentials
function AdminWSBridge({ children }: { children: ReactNode }) {
  const { user } = usePosAuth();
  return (
    <WebSocketProvider userId={user?.id?.toString()} userRole="admin">
      {children}
    </WebSocketProvider>
  );
}

export default function AdminLayout() {
  return (
    <QueryClientProvider client={posQueryClient}>
      <PosAuthProvider>
        <AdminWSBridge>
          <AdminLayoutInner />
        </AdminWSBridge>
        <Toaster />
      </PosAuthProvider>
    </QueryClientProvider>
  );
}
